import { FC, useCallback, useState } from 'react';
import styles from '@sections/send_and_request/manage_contacts/styles.module.scss';
import Button from '@components/common/button';
import Table from '@components/table';
import { apiContactService, apiUserService } from '@api';
import { EContactType, TContact, TInviteUser } from '@xeppt/xeppt-sdk/types';
import { useLocales } from '@hooks/helpers/useLocales';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { useApiMutation } from '@hooks/api/useApiMutation';
import { useSendAndRequestModals } from '@hooks/modals/useSendAndRequestModals';
import { getFirstLetter } from '@utils/index';
import classNames from 'classnames/bind';
import Tabs from '@components/tabs';
import { EContactViewType, EFundsOperation } from '@enum';
import Input from '@components/common/input';
import Info from '@components/common/info';
import Dropdown from '@components/common/dropdown';
import { routes } from '@const/routes';
import { useNavigate } from 'react-router-dom';
import illustration from '@svg/illustrations/empty_contacts.svg';
import illustrationBlocked from '@svg/illustrations/empty_blocked_contacts.svg';
import Skeleton from '@components/common/skeleton';

interface IProps {
    onAdd: () => void;
    refetchContacts: () => void;
    handleEdit: (data: TContact) => void;
    onChangeTab: (data: EContactViewType) => void;
    activeTab: EContactViewType;
    blockedContacts: TContact[];
    contactList: TContact[];
    isLoading?: boolean;
}

const cx = classNames.bind(styles);

const tabs = [EContactViewType.CONTACTS, EContactViewType.BLOCKED];

const ContactsTable: FC<IProps> = ({
    onAdd,
    contactList,
    refetchContacts,
    handleEdit,
    blockedContacts,
    activeTab,
    onChangeTab,
    isLoading
}) => {
    const navigate = useNavigate();
    const { t } = useTranslation('sections', {
        keyPrefix: 'send_and_request.manage_contacts'
    });
    const { requestErrorLocale, requestSuccessLocale, placeholderLocale, submitLocale } =
        useLocales();
    const { handleDeleteContactModal, handleDeleteWalletContact } = useSendAndRequestModals();
    const [search, setSearch] = useState('');

    const { handleRequest: handleUnlockContact } = useApiMutation({
        method: (val: string) => {
            return apiContactService.unblockContact(val);
        },
        onSuccess: () => {
            refetchContacts();
            requestSuccessLocale('unblock_contact');
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleBlockContact } = useApiMutation({
        method: (val: string) => {
            return apiContactService.blockContact(val);
        },
        onSuccess: () => {
            refetchContacts();
            requestSuccessLocale('block_contact');
        },
        onError: requestErrorLocale
    });

    const { handleRequest: handleSendInvite, isLoading: isSendInviteLoading } = useApiMutation({
        method: (data: TInviteUser | TContact) => {
            return apiUserService.sendInvite({
                first_name: data.first_name,
                last_name: data.last_name,
                email: data.email || '',
                phone: data.phone
            });
        },
        onSuccess: () => {
            requestSuccessLocale('contact_invited');
        },
        onError: requestErrorLocale
    });

    const handleSendMoney = (type: EContactType, contact_id: string) => {
        navigate(
            `${routes.send_and_request_send}?type=${EFundsOperation.WALLET_CREDIT_TRANSFER}&contact_id=${contact_id}`
        );
    };

    const handleRequestMoney = (type: EContactType, contact_id: string) => {
        navigate(
            `${routes.send_and_request_request}?type=${EFundsOperation.WALLET_DEBIT_REQUEST}&contact_id=${contact_id}`
        );
    };

    const columns = [
        {
            label: t('name'),
            key: 'first_name',
            render: (_: string, data: TContact, index: number) => (
                <div className={styles.user_wrapper}>
                    <div className={cx([styles.user_avatar, { isDark: index % 2 }])}>
                        {getFirstLetter(data.first_name)}
                        {getFirstLetter(data.last_name)}
                    </div>
                    {`${data.first_name} ${data.last_name}`}
                </div>
            )
        },
        {
            label: 'Username',
            key: 'username',
            render: (val: string) => (val ? `@${val}` : '-')
        },
        {
            label: 'Email',
            key: 'email'
        },
        {
            label: 'Phone number',
            key: 'phone',
            render: (val: string) => (val ? `+1${val}` : '-')
        },
        {
            key: 'actions',
            label: 'Actions',
            render: (_: string, data: TContact) => (
                <div className={styles.table_actions}>
                    {activeTab === EContactViewType.BLOCKED ? (
                        <>
                            <Info maxWidth variant="left" content="Unblock">
                                <Button
                                    leftIcon="block"
                                    onClick={() => handleUnlockContact(data.id)}
                                />
                            </Info>
                            <Info maxWidth variant="left" content="Delete">
                                <Button
                                    leftIcon="delete"
                                    onClick={() => {
                                        if (data.type === EContactType.EXTERNAL) {
                                            handleDeleteContactModal(
                                                data.id,
                                                `${data.first_name} ${data.last_name}`,
                                                refetchContacts
                                            );
                                        } else if (data.type === EContactType.INTERNAL) {
                                            handleDeleteWalletContact(
                                                data.id,
                                                `${data.first_name} ${data.last_name}`,
                                                refetchContacts
                                            );
                                        }
                                    }}
                                />
                            </Info>
                        </>
                    ) : (
                        <>
                            {data?.type === EContactType.EXTERNAL && (
                                <Button
                                    variant="gold"
                                    size="small"
                                    isLoading={isSendInviteLoading}
                                    onClick={() => handleSendInvite(data)}>
                                    Invite to XEPPT
                                </Button>
                            )}
                            <Dropdown
                                items={[
                                    data?.type === EContactType.EXTERNAL && {
                                        label: 'Invite to XEPPT',
                                        icon: 'add_person',
                                        onClick: () => handleSendInvite(data)
                                    },
                                    {
                                        label: submitLocale('send_money'),
                                        icon: 'send',
                                        onClick: () => handleSendMoney(data.type, data.id)
                                    },
                                    {
                                        label: submitLocale('request_money'),
                                        onClick: () => handleRequestMoney(data.type, data.id),
                                        icon: 'request_money'
                                    },
                                    data.type === EContactType.INTERNAL && {
                                        label: submitLocale('block'),
                                        icon: 'block',
                                        onClick: () => handleBlockContact(data.id)
                                    },
                                    {
                                        label: submitLocale('edit'),
                                        icon: 'edit',
                                        onClick: () => handleEdit(data)
                                    },
                                    {
                                        label: submitLocale('delete'),
                                        icon: 'delete',
                                        isRed: true,
                                        onClick: () => {
                                            if (data.type === EContactType.EXTERNAL) {
                                                handleDeleteContactModal(
                                                    data.id,
                                                    `${data.first_name} ${data.last_name}`,
                                                    refetchContacts
                                                );
                                            } else if (data.type === EContactType.INTERNAL) {
                                                handleDeleteWalletContact(
                                                    data.id,
                                                    `${data.first_name} ${data.last_name}`,
                                                    refetchContacts
                                                );
                                            }
                                        }
                                    }
                                ]}
                            />
                        </>
                    )}
                </div>
            )
        }
    ];

    const contactsResults = useCallback(() => {
        const checkIsExist = (contact: TContact) => {
            return (
                contact?.username?.toLowerCase().includes(search.toLowerCase()) ||
                contact?.email?.toLowerCase().includes(search.toLowerCase()) ||
                contact?.phone?.toLowerCase().includes(search.toLowerCase()) ||
                `${contact?.first_name?.toLowerCase()} ${contact?.last_name?.toLowerCase()}`?.includes(
                    search.toLowerCase()
                )
            );
        };
        switch (activeTab) {
            case EContactViewType.BLOCKED:
                return blockedContacts.filter(checkIsExist);
            case EContactViewType.CONTACTS:
            default:
                return contactList.filter(checkIsExist);
        }
    }, [search, blockedContacts, contactList, activeTab]);

    return (
        <>
            <div className={styles.search_wrapper}>
                <Tabs
                    onChange={(val) => onChangeTab(val as EContactViewType)}
                    tabs={tabs.map((item) => ({ label: t(item), value: item }))}
                    activeTab={activeTab}
                    className={styles.tabs}
                />
            </div>
            <div className={styles.content}>
                <div className={styles.search_wrapper}>
                    <Input
                        value={search}
                        onChange={(val) => setSearch(val)}
                        full
                        className={styles.search}
                        leftIcon="search"
                        rightIcon={search.length > 0 ? 'filed_close' : undefined}
                        onClickRightIcon={() => setSearch('')}
                        placeholder={placeholderLocale('search_contact_info')}
                    />
                    <div className={styles.head_actions}>
                        <Button
                            variant="outlined-primary"
                            size="medium"
                            onClick={() => navigate(routes.send_and_request_invite)}>
                            Invite to XEPPT
                        </Button>
                        <Button variant="primary" size="medium" onClick={onAdd}>
                            {t('add_contact_label')}
                        </Button>
                    </div>
                </div>
            </div>
            <motion.div {...pageAnimation} className={styles.content}>
                {isLoading ? (
                    <>
                        <Skeleton className={styles.skeleton} />
                        <Skeleton className={styles.skeleton} />
                        <Skeleton className={styles.skeleton} />
                        <Skeleton className={styles.skeleton} />
                    </>
                ) : (
                    <>
                        {activeTab === EContactViewType.BLOCKED && blockedContacts.length === 0 && (
                            <div className={styles.empty_table}>
                                <img src={illustrationBlocked} alt="" />
                                <div className={styles.title}>No blocked contacts yet</div>
                                <div className={styles.description}>
                                    Once you block a contact, they'll appear here
                                </div>
                            </div>
                        )}
                        {activeTab === EContactViewType.CONTACTS && contactList.length === 0 && (
                            <div className={styles.empty_table}>
                                <img src={illustration} alt="" />
                                <div className={styles.title}>No contacts yet</div>
                                <div className={styles.description}>
                                    Once you add a contact, they'll appear here
                                </div>
                                <Button variant="primary" size="medium" onClick={onAdd}>
                                    {t('add_contact_label')}
                                </Button>
                            </div>
                        )}
                        {((activeTab === EContactViewType.BLOCKED && blockedContacts.length > 0) ||
                            (activeTab === EContactViewType.CONTACTS &&
                                contactList.length > 0)) && (
                            <Table columns={columns} rows={contactsResults()} isActions />
                        )}{' '}
                    </>
                )}
            </motion.div>
        </>
    );
};

export default ContactsTable;
